import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UpgradeCategory = ({ 
  category, 
  isExpanded, 
  onToggle, 
  onAddToCart, 
  onAddToWishlist, 
  onViewDetails 
}) => {
  const [selectedItems, setSelectedItems] = useState([]);

  const handleItemSelect = (itemId) => {
    setSelectedItems(prev => 
      prev?.includes(itemId) 
        ? prev?.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const getPerformanceColor = (rating) => {
    if (rating >= 4.5) return 'text-success';
    if (rating >= 3.5) return 'text-warning';
    return 'text-error';
  };

  const getDifficultyColor = (level) => {
    switch (level) {
      case 'Easy': return 'text-success';
      case 'Medium': return 'text-warning';
      case 'Hard': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Category Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-6 hover:bg-muted/50 transition-colors interactive-scale"
      >
        <div className="flex items-center space-x-4">
          <div className="p-2 bg-accent/10 rounded-lg">
            <Icon name={category?.icon} size={24} color="var(--color-accent)" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-foreground">{category?.name}</h3>
            <p className="text-sm text-muted-foreground">
              {category?.recommendations?.length} recommendations available
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <div className="text-right">
            <div className="text-sm font-medium text-foreground">
              From ${Math.min(...category?.recommendations?.map(r => r?.vendors?.[0]?.price))}
            </div>
            <div className="text-xs text-muted-foreground">
              Avg. {category?.avgRating}/5 rating
            </div>
          </div>
          <Icon 
            name={isExpanded ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            color="var(--color-muted-foreground)" 
          />
        </div>
      </button>
      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-border animate-fade-in">
          <div className="p-6 space-y-6">
            {category?.recommendations?.map((recommendation) => (
              <div key={recommendation?.id} className="bg-muted/30 rounded-lg p-4">
                {/* Recommendation Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h4 className="text-base font-semibold text-foreground mb-1">
                      {recommendation?.name}
                    </h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      {recommendation?.description}
                    </p>
                    
                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="inline-flex items-center px-2 py-1 bg-success/10 text-success text-xs rounded-full">
                        <Icon name="CheckCircle" size={12} className="mr-1" />
                        Compatible
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 bg-muted text-xs rounded-full ${getDifficultyColor(recommendation?.installDifficulty)}`}>
                        <Icon name="Wrench" size={12} className="mr-1" />
                        {recommendation?.installDifficulty} Install
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 bg-muted text-xs rounded-full ${getPerformanceColor(recommendation?.performanceRating)}`}>
                        <Icon name="Zap" size={12} className="mr-1" />
                        {recommendation?.performanceRating}/5 Performance
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-right ml-4">
                    <div className="text-lg font-bold text-foreground">
                      ${recommendation?.vendors?.[0]?.price}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      from {recommendation?.vendors?.length} vendors
                    </div>
                  </div>
                </div>

                {/* Vendor Comparison Table */}
                <div className="overflow-x-auto mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 text-muted-foreground font-medium">Vendor</th>
                        <th className="text-right py-2 text-muted-foreground font-medium">Price</th>
                        <th className="text-center py-2 text-muted-foreground font-medium">Shipping</th>
                        <th className="text-center py-2 text-muted-foreground font-medium">Stock</th>
                        <th className="text-center py-2 text-muted-foreground font-medium">Rating</th>
                        <th className="text-center py-2 text-muted-foreground font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recommendation?.vendors?.map((vendor, index) => (
                        <tr key={vendor?.id} className="border-b border-border/50">
                          <td className="py-3">
                            <div className="flex items-center space-x-2">
                              <img 
                                src={vendor?.logo} 
                                alt={vendor?.name}
                                className="w-6 h-6 rounded object-cover"
                              />
                              <span className="font-medium text-foreground">{vendor?.name}</span>
                              {vendor?.isAffiliate && (
                                <span className="text-xs bg-accent/10 text-accent px-1 py-0.5 rounded">
                                  Partner
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="text-right py-3">
                            <div className="font-semibold text-foreground">${vendor?.price}</div>
                            {index > 0 && (
                              <div className="text-xs text-error">
                                +${vendor?.price - recommendation?.vendors?.[0]?.price}
                              </div>
                            )}
                          </td>
                          <td className="text-center py-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              vendor?.shipping === 'Free' ?'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                            }`}>
                              {vendor?.shipping}
                            </span>
                          </td>
                          <td className="text-center py-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              vendor?.inStock 
                                ? 'bg-success/10 text-success' :'bg-error/10 text-error'
                            }`}>
                              {vendor?.inStock ? 'In Stock' : 'Out of Stock'}
                            </span>
                          </td>
                          <td className="text-center py-3">
                            <div className="flex items-center justify-center space-x-1">
                              <Icon name="Star" size={12} color="var(--color-warning)" />
                              <span className="text-xs font-medium">{vendor?.rating}</span>
                            </div>
                          </td>
                          <td className="text-center py-3">
                            <Button
                              variant="outline"
                              size="xs"
                              onClick={() => onAddToCart(recommendation, vendor)}
                              disabled={!vendor?.inStock}
                            >
                              Add to Cart
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Heart"
                      iconPosition="left"
                      onClick={() => onAddToWishlist(recommendation)}
                    >
                      Save to Wishlist
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="MapPin"
                      iconPosition="left"
                      onClick={() => onViewDetails(recommendation, 'local-installer')}
                    >
                      Find Local Installer
                    </Button>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Bell"
                      iconPosition="left"
                      onClick={() => onViewDetails(recommendation, 'price-alert')}
                    >
                      Price Alert
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      iconName="ExternalLink"
                      iconPosition="right"
                      onClick={() => onViewDetails(recommendation, 'details')}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UpgradeCategory;